import { useState, useEffect } from "react";
import axios from "axios";
import axiosClient from "../../axios";
const GenerateActivationCodes = () => {
  const [formData, setFormData] = useState({
    count: 10,
    batch_name: "",
    expires_at: "",
    prefix: "XLM",
    affiliate_id: "",
  });

  const [distributors, setDistributors] = useState([]);
  const [codes, setCodes] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    fetchDistributors();
  }, []);

  const fetchDistributors = async () => {
    try {
      const response = await axiosClient.get("/api/users/distributors");
      setDistributors(response.data || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to load distributors");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");
    setCodes([]);
    setDownloadUrl("");

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/codes/generate`,
        {
          count: formData.count,
          batch_name: formData.batch_name,
          expires_at: formData.expires_at || null,
          prefix: formData.prefix,
          affiliate_id: formData.affiliate_id,
        },
      );

      if (response.data.success) {
        setSuccess("Activation codes generated successfully");
        setCodes(response.data.codes || []);
      } else {
        throw new Error(response.data.message || "Failed to generate codes");
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      alert("Codes copied");
    } catch {
      alert("Failed to copy");
    }
  };

  const downloadExcel = async () => {
    if (codes.length === 0) {
      alert("Generate codes first");
      return;
    }

    try {
      setIsDownloading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/codes/download-excel`,
        {
          codes,
        },
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      setDownloadUrl(url);

      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", `activation-codes-${Date.now()}.xlsx`);

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      alert("Failed to download excel");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="container">
      <h3 className="mb-4">Generate Activation Codes</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Number of Codes */}
          <div className="col-md-4 mb-3">
            <label className="form-label">Number of Codes</label>

            <input
              type="number"
              name="count"
              className="form-control"
              min="1"
              max="1000"
              value={formData.count}
              onChange={handleChange}
              required
            />
          </div>

          {/* Prefix */}
          <div className="col-md-4 mb-3">
            <label className="form-label">Code Prefix</label>

            <input
              type="text"
              name="prefix"
              className="form-control"
              placeholder="XLM"
              value={formData.prefix}
              onChange={handleChange}
            />
          </div>

          {/* Batch Name */}
          <div className="col-md-4 mb-3">
            <label className="form-label">Batch Name</label>

            <input
              type="text"
              name="batch_name"
              className="form-control"
              placeholder="launch_offer"
              value={formData.batch_name}
              onChange={handleChange}
            />
          </div>

          {/* Expiry */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Expiry Date</label>

            <input
              type="datetime-local"
              name="expires_at"
              className="form-control"
              value={formData.expires_at}
              onChange={handleChange}
            />
          </div>

          {/* Distributor */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Distributor</label>

            <select
              name="affiliate_id"
              className="form-select"
              value={formData.affiliate_id}
              onChange={handleChange}
            >
              <option value="">Select Distributor</option>

              {distributors.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.phone || user.email})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 flex-wrap">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`btn btn-primary ${isSubmitting ? "disabled" : ""}`}
          >
            {isSubmitting ? "Generating..." : "Generate Codes"}
          </button>

          <button
            type="button"
            onClick={downloadExcel}
            disabled={isDownloading || codes.length === 0}
            className={`btn btn-warning ${isDownloading ? "disabled" : ""}`}
          >
            {isDownloading ? "Downloading..." : "Download Excel"}
          </button>
        </div>
      </form>

      {downloadUrl && (
        <div className="mt-3">
          <a href={downloadUrl} download className="btn btn-outline-secondary">
            Download Again
          </a>
        </div>
      )}

      {codes.length > 0 && (
        <div className="card mt-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>Generated Codes ({codes.length})</span>

            <button
              className="btn btn-sm btn-outline-primary"
              onClick={copyCodes}
            >
              Copy All
            </button>
          </div>

          <div className="card-body">
            <div className="row">
              {codes.map((code, index) => (
                <div key={index} className="col-md-4 mb-2">
                  <div className="border rounded p-2 bg-light">
                    <strong>{code}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateActivationCodes;
